import type { AllowanceBasis, AllowanceLine, PenaltyBand, PenaltyLine } from '../award-rate-lookup';

// 1.0.8 — overtime tier blocks compile `overtimeOnLoadedRate` metadata for
// GS cl 32(c)(i) and Nurses cl 22.22 (8 Sep M1/M3); threshold logic stays engine-interpreter.
// 1.0.9 — overnight day-type band splitter emits positive-duration bands only
// (8 Sep M5 phantom midnight band); cap cascade helpers live in
// ordinary-hours-cap-windows.ts (M6).
// 1.0.10 — explicit casual penalty totals carry `includesCasualLoading` so
// engines never compound the 25% again (8 Sep M7); OT shift-loading drop
// default comes from `retainShiftLoadingOnOvertime` on the compile profile.
// 1.0.11 — BASE_PLUS explicit-casual blocks emit base multipliers with
// includesCasualLoading until governed rematerialisation (P45).
// 1.0.12 — GA/NEA weekday OT tier blocks default overtimeOnLoadedRate on
// without clauseRef (live pay_rule_blocks often omit the string).
// 1.0.13 — P60: BASE_PLUS divided casual penalties omit includesCasualLoading
// on base multipliers; flag true means already loaded (rematerialised rows).
// 1.0.14 — 2026-09-17 Nurses EA re-derivation: overtimeOnLoadedRateForBlock
// never defaults MA000034 on (NEA_OVERTIME_TIERS block code and "22.22"
// clauseRef withdrawn as authority). GS cl 32(c)(i) unaffected.
// 1.0.15 — recognise the persisted GS daily OVERTIME pair; additive loaded-rate basis.
// 1.0.16 — accept the actual threshold-free remainder; refuse ambiguous identities.
export const EBA_BLOCK_COMPILER_ADAPTER_VERSION = '1.0.16';

export type PayRuleBlockAutomationStatus =
  | 'AUTOMATED'
  | 'MANUAL_ONLY'
  | 'UNSUPPORTED'
  | 'DISABLED'
  | string;

export interface PayRuleBlock {
  id?: string | null;
  block_code: string;
  block_kind: string;
  title?: string | null;
  automation_status?: PayRuleBlockAutomationStatus | null;
  required_fact_codes?: string[] | null;
  export_component_codes?: string[] | null;
  parameters?: Record<string, unknown> | null;
}

export type CasualPenaltyConvention = 'INCLUSIVE' | 'BASE_PLUS_ENGINE_LOADING';

export interface CompileProfile {
  baseAwardCode: string;
  casualPenaltyConvention: CasualPenaltyConvention;
  casualLoadingRate: number;
  ctxReadableBands: readonly PenaltyBand[];
  findAllowanceCodes: readonly string[];
  /**
   * When false (default), weekday overtime is in substitution for shift
   * loadings (Nurses cl 19.1(c) / Medea M4). Set true only when the EA
   * explicitly retains loading on OT hours.
   */
  retainShiftLoadingOnOvertime?: boolean;
}

export interface CompiledPenaltyLine extends PenaltyLine {
  sourceBlockCode: string;
  /**
   * When true, `multiplier` is already the casual total rate (includes the
   * 25% loading). The engine must not multiply by `(1 + casualLoadingRate)`
   * again.
   */
  includesCasualLoading?: boolean;
}

export interface CompiledAllowanceLine extends AllowanceLine {
  sourceBlockCode: string;
}

export type CompiledConditionalPenaltyKind =
  | 'SHIFT_START_WINDOW'
  | 'MEAL_BREAK'
  | 'INTERRUPTED_MEAL_BREAK'
  | 'PUBLIC_HOLIDAY_NOT_WORKED';

/**
 * How a `startWindow` on a shift-loading conditional penalty is tested against
 * the shift. This is the data-driven "mechanic" — an EA declares it per block,
 * so a new provider's custom agreement expresses its shift-loading semantics in
 * DATA, with no engine change:
 * - `COMMENCEMENT` (default when absent) — the shift COMMENCES inside the
 *   window (e.g. General EA cl 6 day-worker bands; the legacy behaviour).
 * - `SPAN` — the shift works SOME OR ALL hours inside the window, i.e. the
 *   worked interval OVERLAPS the window (General EA cl 44 rostered-employee
 *   loading).
 * - `FINISH` — the shift FINISHES inside the window (e.g. Nurses EA cl 22.1
 *   finish-time definitions).
 * In every mode the loading is paid on the whole shift's ordinary hours; only
 * the qualification test differs. Absent → COMMENCEMENT, so pre-existing
 * compiled data is unchanged.
 */
export type CompiledShiftWindowMode = 'COMMENCEMENT' | 'SPAN' | 'FINISH';

export interface CompiledConditionalPenaltyLine {
  blockCode: string;
  blockKind: string;
  kind: CompiledConditionalPenaltyKind;
  employmentType: CompiledPenaltyEmployment;
  multiplier: number;
  employeeCategories?: string[];
  startWindow?: readonly [string, string];
  /** Window test mode for `startWindow`. Absent = COMMENCEMENT (legacy). */
  windowMode?: CompiledShiftWindowMode;
  triggerContinuousHours?: number;
  clauseRef?: string;
  description?: string;
  /**
   * When true, `multiplier` already includes casual loading and the engine
   * must not apply `(1 + casualLoadingRate)` again.
   */
  includesCasualLoading?: boolean;
}

export interface ManualBlockContract {
  blockCode: string;
  blockKind: string;
  automationStatus: string;
  requiredFactCodes: string[];
  exportComponentCodes: string[];
  requiredAllowanceCodes: string[];
  reason: string;
}

export interface StructuralBlock {
  blockCode: string;
  blockKind: string;
  automationStatus: string;
  reason: string;
  parameters: Record<string, unknown>;
}

/**
 * Metadata extracted from EA overtime tier blocks. Threshold/cycle logic remains
 * in the award engine interpreter; this carries multipliers and whether OT is
 * calculated on the shift-loaded hourly rate (GS cl 32(c)(i), Nurses cl 22.22).
 */
export interface CompiledOvertimeRule {
  sourceBlockCode: string;
  firstTwoHoursMultiplier: number;
  afterTwoHoursMultiplier: number;
  /** When true, OT multipliers apply to base × (1 + shift loading), not base + loading on base. */
  overtimeOnLoadedRate: boolean;
  /** Persisted GS daily pair: casual and shift premiums add before the OT tier. */
  shiftLoadingIsAdditive?: boolean;
  clauseRef?: string;
}

export interface CompiledEbaOverlay {
  penalties: CompiledPenaltyLine[];
  allowances: CompiledAllowanceLine[];
  conditionalPenalties: CompiledConditionalPenaltyLine[];
  overtimeRules: CompiledOvertimeRule[];
  requiredBands: string[];
  requiredAllowanceCodes: string[];
  requiredConditionalPenaltyCodes: string[];
  manualContracts: ManualBlockContract[];
  structuralUnsupported: StructuralBlock[];
  /**
   * EA-supplied afternoon/night shift-character windows (e.g. Nurses EA
   * cl 22.1 finish-time). Present only when a block declares them; the engine
   * then uses these instead of its base-award defaults. Data-driven, so a new
   * provider's agreement defines its own windows without an engine change.
   */
  shiftCharacterRules?: import('../award-engines/shift-classifier').ShiftCharacterRules;
}

const PENALTY_BAND_ORDER: PenaltyBand[] = [
  'WEEKDAY_DAY',
  'WEEKDAY_EVENING',
  'WEEKDAY_AFTERNOON',
  'WEEKDAY_NIGHT',
  'PERMANENT_NIGHT_SHIFT',
  'SATURDAY',
  'SUNDAY',
  'PUBLIC_HOLIDAY',
  'OVERTIME_FIRST_2H',
  'OVERTIME_AFTER_2H',
];

const OUTPUT_BAND_ORDER: PenaltyBand[] = [
  'SATURDAY',
  'SUNDAY',
  'PUBLIC_HOLIDAY',
  'WEEKDAY_AFTERNOON',
  'WEEKDAY_NIGHT',
  'PERMANENT_NIGHT_SHIFT',
  'WEEKDAY_DAY',
  'WEEKDAY_EVENING',
  'OVERTIME_FIRST_2H',
  'OVERTIME_AFTER_2H',
];

const KNOWN_PENALTY_BANDS = new Set<PenaltyBand>(PENALTY_BAND_ORDER);
const EMPLOYMENT_ORDER = ['PERMANENT', 'CASUAL'] as const;
type CompiledPenaltyEmployment = (typeof EMPLOYMENT_ORDER)[number];

interface CollectedBandMultiplier {
  band: PenaltyBand;
  multiplier: number;
  employmentType: CompiledPenaltyEmployment | null;
}

type PenaltyLineSpecificity = 'GENERIC' | 'EXPLICIT_EMPLOYMENT';

const CONFLICTING_PENALTY_MULTIPLIERS_REASON = 'CONFLICTING_PENALTY_MULTIPLIERS_FOR_BAND';
const NON_WORKED_PENALTY_VARIANT_REASON = 'NON_WORKED_PENALTY_VARIANT_NOT_MODELLED';
const CONDITIONAL_MULTIPLIER_TOTAL_RATE_REASON = 'CONDITIONAL_MULTIPLIER_MUST_BE_TOTAL_RATE';

export const EBA_COMPILE_PROFILES: Record<string, CompileProfile> = {
  MA000018: {
    baseAwardCode: 'MA000018',
    casualPenaltyConvention: 'INCLUSIVE',
    casualLoadingRate: 0.25,
    retainShiftLoadingOnOvertime: false,
    ctxReadableBands: ['SATURDAY', 'SUNDAY', 'PUBLIC_HOLIDAY'],
    findAllowanceCodes: [
      'LEADING_HAND_2_5',
      'LEADING_HAND_6_10',
      'LEADING_HAND_11_15',
      'LEADING_HAND_16_PLUS',
      'SLEEPOVER',
      'KM',
      'UNIFORM_PER_SHIFT',
      'LAUNDRY_PER_SHIFT',
      'NAUSEOUS_WORK',
      'MEAL',
      'MEAL_OT_ADDITIONAL',
    ],
  },
  MA000034: {
    baseAwardCode: 'MA000034',
    casualPenaltyConvention: 'BASE_PLUS_ENGINE_LOADING',
    casualLoadingRate: 0.25,
    retainShiftLoadingOnOvertime: false,
    ctxReadableBands: [
      'SATURDAY',
      'SUNDAY',
      'PUBLIC_HOLIDAY',
      'WEEKDAY_AFTERNOON',
      'WEEKDAY_NIGHT',
      'PERMANENT_NIGHT_SHIFT',
    ],
    findAllowanceCodes: [
      'KM',
      'ON_CALL_WEEKDAY',
      'ON_CALL_SATURDAY',
      'ON_CALL_SUNDAY_PH',
      'ON_CALL_WEEKEND',
      'UNIFORM_PER_SHIFT',
      'LAUNDRY_PER_SHIFT',
      'MEAL',
      'MEAL_OT_ADDITIONAL',
    ],
  },
};

const DIRECT_BAND_PARAM_ALIASES: Array<[PenaltyBand, string[]]> = [
  [
    'SATURDAY',
    [
      'saturdayMultiplier',
      'saturdayRate',
      'saturdayWorkedMultiplier',
      'saturdayShiftMultiplier',
      'satMultiplier',
      'satRate',
      'satWorkedMultiplier',
      'satShiftMultiplier',
      'saturdayCasualMultiplier',
      'saturdayCasualWorkedMultiplier',
      'saturdayCasualShiftMultiplier',
    ],
  ],
  [
    'SUNDAY',
    [
      'sundayMultiplier',
      'sundayRate',
      'sundayWorkedMultiplier',
      'sundayShiftMultiplier',
      'sunMultiplier',
      'sunRate',
      'sunWorkedMultiplier',
      'sunShiftMultiplier',
      'sundayCasualMultiplier',
      'sundayCasualWorkedMultiplier',
      'sundayCasualShiftMultiplier',
    ],
  ],
  [
    'PUBLIC_HOLIDAY',
    [
      'publicHolidayMultiplier',
      'publicHolidayRate',
      'publicHolidayDayworkerMultiplier',
      'publicHolidayWorkedMultiplier',
      'publicHolidayShiftMultiplier',
      'publicHolidayCasualMultiplier',
      'publicHolidayCasualDayworkerMultiplier',
      'publicHolidayCasualWorkedMultiplier',
      'publicHolidayCasualShiftMultiplier',
      'phMultiplier',
      'phRate',
      'phWorkedMultiplier',
      'phShiftMultiplier',
      'holidayMultiplier',
      'holidayWorkedMultiplier',
      'holidayShiftMultiplier',
    ],
  ],
  [
    'WEEKDAY_EVENING',
    [
      'eveningMultiplier',
      'eveningRate',
      'eveningShiftMultiplier',
      'weekdayEveningMultiplier',
      'weekdayEveningShiftMultiplier',
    ],
  ],
  [
    'WEEKDAY_AFTERNOON',
    [
      'afternoonMultiplier',
      'afternoonRate',
      'afternoonShiftMultiplier',
      'weekdayAfternoonMultiplier',
      'weekdayAfternoonShiftMultiplier',
    ],
  ],
  [
    'WEEKDAY_NIGHT',
    [
      'nightMultiplier',
      'nightRate',
      'nightShiftMultiplier',
      'weekdayNightMultiplier',
      'weekdayNightShiftMultiplier',
    ],
  ],
  [
    'PERMANENT_NIGHT_SHIFT',
    [
      'permanentNightMultiplier',
      'permanentNightShiftMultiplier',
      'permanentNightRate',
      'permanentNightShiftRate',
    ],
  ],
];

function normalizedUpper(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim().toUpperCase() : null;
}

function normalizedText(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function numericParam(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : [];
}

function canonicalEmployeeCategory(value: unknown): string | null {
  const normalized = normalizedUpper(value);
  if (!normalized) return null;
  if (normalized === 'DAYWORKER' || normalized === 'DAY_WORKER') return 'DAYWORKER';
  if (
    normalized === 'SHIFTWORKER' ||
    normalized === 'SHIFT_WORKER' ||
    normalized === 'ROTATING_ROSTER' ||
    normalized === 'PERMANENT_NIGHT' ||
    normalized === 'PERMANENT_NIGHT_SHIFT'
  ) {
    return 'SHIFTWORKER';
  }
  return normalized;
}

function conditionalEmployeeCategories(block: PayRuleBlock): string[] {
  const params = block.parameters ?? {};
  const configured = params.employeeCategories ?? params.employeeCategory;
  const values = Array.isArray(configured) ? configured : configured == null ? [] : [configured];
  return Array.from(
    new Set(
      values
        .map((value) => canonicalEmployeeCategory(value))
        .filter((value): value is string => Boolean(value)),
    ),
  ).sort();
}

function blockStatus(block: PayRuleBlock): string {
  return normalizedUpper(block.automation_status ?? 'AUTOMATED') ?? 'AUTOMATED';
}

function blockKind(block: PayRuleBlock): string {
  return normalizedUpper(block.block_kind) ?? '';
}

function blockCodeAndTitle(block: PayRuleBlock): string {
  return `${block.block_code} ${block.title ?? ''}`.toUpperCase();
}

function normalizedTokenText(value: string): { normalized: string; tokens: string[] } {
  const normalized = value.toUpperCase().replace(/[^A-Z0-9]+/g, '_');
  return {
    normalized,
    tokens: normalized.split('_').filter(Boolean),
  };
}

function canonicalPenaltyBand(value: unknown): PenaltyBand | null {
  const normalized = normalizedUpper(value);
  if (!normalized) return null;
  const mapped =
    normalized === 'SAT' || normalized === 'SATURDAY_ORDINARY'
      ? 'SATURDAY'
      : normalized === 'SUN' || normalized === 'SUNDAY_ORDINARY'
        ? 'SUNDAY'
        : normalized === 'PH' ||
            normalized === 'PUBLICHOLIDAY' ||
            normalized === 'PUBLIC_HOLIDAY_WORKED'
          ? 'PUBLIC_HOLIDAY'
          : normalized === 'AFTERNOON' || normalized === 'AFTERNOON_SHIFT'
            ? 'WEEKDAY_AFTERNOON'
            : normalized === 'NIGHT' || normalized === 'NIGHT_SHIFT'
              ? 'WEEKDAY_NIGHT'
              : normalized === 'PERMANENT_NIGHT' || normalized === 'PERMANENT_NIGHT_WORKER'
                ? 'PERMANENT_NIGHT_SHIFT'
                : normalized;
  return KNOWN_PENALTY_BANDS.has(mapped as PenaltyBand) ? (mapped as PenaltyBand) : null;
}

function bandFromText(value: string): PenaltyBand | null {
  const { normalized, tokens } = normalizedTokenText(value);
  const tokenSet = new Set(tokens);

  if (
    normalized.includes('NOT_WORKED') ||
    normalized.includes('STAND_DOWN') ||
    normalized.includes('ABSENCE') ||
    normalized.includes('ABSENT')
  ) {
    return null;
  }

  if (
    normalized.includes('PUBLIC_HOLIDAY') ||
    normalized.includes('PUBLICHOLIDAY') ||
    tokenSet.has('PH')
  ) {
    return 'PUBLIC_HOLIDAY';
  }
  if (normalized.includes('PERMANENT_NIGHT')) return 'PERMANENT_NIGHT_SHIFT';
  if (tokenSet.has('SATURDAY') || tokenSet.has('SAT')) return 'SATURDAY';
  if (tokenSet.has('SUNDAY') || tokenSet.has('SUN')) return 'SUNDAY';
  if (tokenSet.has('AFTERNOON')) return 'WEEKDAY_AFTERNOON';
  if (tokenSet.has('NIGHT')) return 'WEEKDAY_NIGHT';
  if (tokenSet.has('EVENING')) return 'WEEKDAY_EVENING';
  return null;
}

function trueParam(value: unknown): boolean {
  return value === true || normalizedUpper(value) === 'TRUE';
}

function nonWorkedPenaltyVariantReason(block: PayRuleBlock): string | null {
  const params = block.parameters ?? {};
  if (
    trueParam(params.appliesWhenNormallyRostered) ||
    trueParam(params.applies_when_normally_rostered)
  ) {
    return NON_WORKED_PENALTY_VARIANT_REASON;
  }

  const code = normalizedTokenText(block.block_code).normalized;
  if (code.includes('NOT_WORKED')) return NON_WORKED_PENALTY_VARIANT_REASON;

  const { normalized, tokens } = normalizedTokenText(blockCodeAndTitle(block));
  const tokenSet = new Set(tokens);
  const publicHolidayLike =
    normalized.includes('PUBLIC_HOLIDAY') ||
    normalized.includes('PUBLICHOLIDAY') ||
    tokenSet.has('PH');
  const notWorkedLike =
    normalized.includes('NOT_WORKED') ||
    normalized.includes('NOT_WORK') ||
    normalized.includes('NON_WORKED') ||
    (tokenSet.has('NOT') && tokenSet.has('WORKED'));
  const absenceLike =
    normalized.includes('STAND_DOWN') ||
    tokenSet.has('STANDDOWN') ||
    tokenSet.has('ABSENCE') ||
    tokenSet.has('ABSENT');

  if ((publicHolidayLike && notWorkedLike) || absenceLike) {
    return NON_WORKED_PENALTY_VARIANT_REASON;
  }

  if (publicHolidayLike && normalized.includes('NORMALLY_ROSTERED')) {
    return NON_WORKED_PENALTY_VARIANT_REASON;
  }

  return null;
}

function inferredPenaltyBand(block: PayRuleBlock, parameterName?: string): PenaltyBand | null {
  if (nonWorkedPenaltyVariantReason(block)) return null;

  const params = block.parameters ?? {};
  const explicit = canonicalPenaltyBand(
    params.band ?? params.penaltyBand ?? params.shiftBand ?? params.dayType ?? params.day_type,
  );
  if (explicit) return explicit;
  return bandFromText(`${parameterName ?? ''} ${blockCodeAndTitle(block)}`);
}

// Tokens that, immediately preceding CASUAL in a block code/title, invert its
// meaning. Kept to forms whose negation is unambiguous — see the note at the
// use site for why EXCLUSIVE is excluded.
const CASUAL_NEGATION_TOKENS = new Set(['NON', 'NOT', 'NO', 'EXCEPT', 'EXCL', 'EXCLUDING']);

function employmentTypeForMultiplier(
  block: PayRuleBlock,
  parameterName?: string,
): CompiledPenaltyEmployment | null {
  const params = block.parameters ?? {};
  const explicit = normalizedUpper(
    params.employmentType ??
      params.employment_type ??
      params.appliesToEmployment ??
      params.applies_to_employment,
  );
  if (explicit === 'CASUAL' || explicit === 'PERMANENT') return explicit;

  // Name heuristic — an agreement may target casuals by naming the block
  // rather than by parameter. Because a CASUAL target suppresses casual-rate
  // derivation (the declared value is taken as the casual TOTAL), a false
  // positive here silently mis-prices the block, so NEGATED forms must not
  // match: `NON_CASUAL_AFTERNOON` tokenises to ['NON','CASUAL','AFTERNOON']
  // and would otherwise be read as casual-targeted. (`CASUALTY` is already
  // safe — it is its own token and never equals 'CASUAL'.)
  const tokens = `${parameterName ?? ''} ${blockCodeAndTitle(block)}`
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .split('_');
  const casualAt = tokens.indexOf('CASUAL');
  if (casualAt === -1) return null;
  const preceding = casualAt > 0 ? tokens[casualAt - 1] : null;
  // Unambiguous negations only. `EXCLUSIVE_CASUAL` is deliberately NOT here:
  // it reads as "exclusively casual", i.e. casual-TARGETED, so negating it
  // would invert the intended meaning.
  if (CASUAL_NEGATION_TOKENS.has(preceding ?? '')) return null;
  return 'CASUAL';
}

function sourceDescription(block: PayRuleBlock, band: PenaltyBand): string {
  const title = normalizedText(block.title);
  if (title) return title;
  return `${block.block_code} ${band}`;
}

function bandOrder(band: string): number {
  const index = OUTPUT_BAND_ORDER.indexOf(band as PenaltyBand);
  return index === -1 ? OUTPUT_BAND_ORDER.length : index;
}

function addUniqueSorted(target: Set<string>, value: string | null | undefined): void {
  if (value && value.trim().length > 0) target.add(value.trim());
}

function casualMultiplierFor(profile: CompileProfile, permanentMultiplier: number): number {
  if (profile.casualPenaltyConvention === 'BASE_PLUS_ENGINE_LOADING') {
    return permanentMultiplier;
  }

  return permanentMultiplier + profile.casualLoadingRate;
}

interface CompiledPenaltyMultiplier {
  multiplier: number;
  includesCasualLoading: boolean;
}

function compiledPenaltyMultiplierFor(
  profile: CompileProfile,
  multiplierValue: number,
  targetEmploymentType: CompiledPenaltyEmployment | null,
  employmentType: CompiledPenaltyEmployment,
): CompiledPenaltyMultiplier {
  if (targetEmploymentType === null && employmentType === 'CASUAL') {
    return {
      multiplier: casualMultiplierFor(profile, multiplierValue),
      includesCasualLoading: false,
    };
  }

  if (targetEmploymentType === 'CASUAL') {
    if (profile.casualPenaltyConvention === 'BASE_PLUS_ENGINE_LOADING') {
      // Block declares the casual total (e.g. Medea Nurses 1.875). Persisted
      // rows stay at the BASE multiplier (1.5) until rematerialisation, so the
      // compiled line is the base too and the engine applies the loading once.
      // `includesCasualLoading` means "this multiplier already carries the
      // loading" — it must be FALSE here, otherwise readers that honour the
      // flag (engine, cert reducer, drift detector) drop the loading entirely
      // (integrator fix 16 Sep: the P57 fold priced Medea casual Saturday at
      // 1.5 instead of 1.875 because the flag was set on a divided-out base).
      return {
        multiplier: multiplierValue / (1 + profile.casualLoadingRate),
        includesCasualLoading: false,
      };
    }
    // INCLUSIVE profile: declared casual total is authoritative.
    return { multiplier: multiplierValue, includesCasualLoading: true };
  }

  return { multiplier: multiplierValue, includesCasualLoading: false };
}

/** Default cl 19.1(c) substitution — drop shift loading on OT unless EA opts in. */
export function retainShiftLoadingOnOvertimeForProfile(baseAwardCode: string): boolean {
  return EBA_COMPILE_PROFILES[baseAwardCode]?.retainShiftLoadingOnOvertime === true;
}

function addPenaltyLine(
  lines: Map<string, CompiledPenaltyLine>,
  lineSourceBlocks: Map<string, PayRuleBlock>,
  lineSpecificities: Map<string, PenaltyLineSpecificity>,
  requiredBands: Set<string>,
  structuralUnsupported: StructuralBlock[],
  profile: CompileProfile,
  block: PayRuleBlock,
  band: PenaltyBand,
  multiplierValue: number,
  targetEmploymentType: CompiledPenaltyEmployment | null = null,
): void {
  if (!profile.ctxReadableBands.includes(band)) return;
  requiredBands.add(band);

  const description = sourceDescription(block, band);
  const employmentTypes = targetEmploymentType ? [targetEmploymentType] : EMPLOYMENT_ORDER;
  for (const employmentType of employmentTypes) {
    const compiled = compiledPenaltyMultiplierFor(
      profile,
      multiplierValue,
      targetEmploymentType,
      employmentType,
    );
    const key = `${band}|${employmentType}|`;
    const roundedMultiplier = roundMultiplier(compiled.multiplier);
    const specificity: PenaltyLineSpecificity =
      targetEmploymentType === null ? 'GENERIC' : 'EXPLICIT_EMPLOYMENT';
    const existing = lines.get(key);
    const existingSpecificity = lineSpecificities.get(key);

    if (existing && existingSpecificity === 'EXPLICIT_EMPLOYMENT' && specificity === 'GENERIC') {
      continue;
    }

    if (existing && existing.multiplier !== roundedMultiplier) {
      if (existingSpecificity === 'GENERIC' && specificity === 'EXPLICIT_EMPLOYMENT') {
        lines.set(key, {
          band,
          employmentType,
          classificationStream: null,
          multiplier: roundedMultiplier,
          description,
          sourceBlockCode: block.block_code,
          ...(compiled.includesCasualLoading ? { includesCasualLoading: true } : {}),
        });
        lineSourceBlocks.set(key, block);
        lineSpecificities.set(key, specificity);
        continue;
      }

      const existingBlock = lineSourceBlocks.get(key);
      if (existingBlock) {
        pushStructuralUnsupportedOnce(
          structuralUnsupported,
          existingBlock,
          CONFLICTING_PENALTY_MULTIPLIERS_REASON,
        );
      }
      pushStructuralUnsupportedOnce(
        structuralUnsupported,
        block,
        CONFLICTING_PENALTY_MULTIPLIERS_REASON,
      );
      continue;
    }

    lines.set(key, {
      band,
      employmentType,
      classificationStream: null,
      multiplier: roundedMultiplier,
      description,
      sourceBlockCode: block.block_code,
      ...(compiled.includesCasualLoading ? { includesCasualLoading: true } : {}),
    });
    lineSourceBlocks.set(key, block);
    lineSpecificities.set(key, specificity);
  }
}

function conditionalTotalMultiplier(
  profile: CompileProfile,
  multiplierValue: number,
  employmentType: CompiledPenaltyEmployment,
  kind: CompiledConditionalPenaltyKind,
  targetEmploymentType: CompiledPenaltyEmployment | null,
): number {
  if (employmentType !== 'CASUAL') return multiplierValue;

  // Never DERIVE a casual rate the agreement already DECLARED. When a block
  // explicitly targets casuals — `employmentType: 'CASUAL'`, or a block
  // code/title containing "CASUAL" — its multiplier is already the casual TOTAL
  // rate, so adding or compounding the casual loading double-counts it.
  //
  // This is a data-contract invariant, not a clause interpretation: it holds for
  // every agreement and every conditional kind. The day-type penalty path has
  // always enforced it (see `compiledPenaltyMultiplierFor`, which only derives
  // when `targetEmploymentType === null`); the conditional path did not, so an
  // EA declaring e.g. a 1.40 casual cl 44 line compiled to 1.75 under the old
  // multiplicative treatment. That is a provider-onboarding footgun rather than
  // a Medea issue — Medea declares base rates and lets the compiler derive —
  // but it must be closed before a second MA000018 agreement is onboarded.
  if (targetEmploymentType === 'CASUAL') return multiplierValue;

  // Shift loadings under an INCLUSIVE-casual profile are ADDITIVE with the
  // casual loading, not compounded on top of it.
  //
  // Primary source — Medea Park General Staff EA 2024 ([2025] FWCA 1968):
  //   cl 31 (Modes of Employment — Casual employees):
  //     "A casual employee will be paid per hour worked at the rate of 1/38th
  //      of the weekly rate appropriate to the employee's classification. In
  //      addition, a loading of 25% of that rate will be paid…"
  //     "A casual employee shall be entitled to the penalty rates provided for
  //      under this agreement as applied to a full time employee."
  //   cl 44 (Shift Loading — Rostered Employee): afternoon "15% of the ordinary
  //     hourly rate"; night "17.5% of the ordinary hourly rate".
  //   cl 6 (definitions) day-worker bands: 10% / 12.5% / 15% / 10% — each
  //     likewise "of the ordinary hourly rate".
  //
  // The casual loading is paid "in addition" to the 1/38th ordinary hourly
  // rate; it is not part of it. A loading expressed as a percentage "of the
  // ordinary hourly rate" therefore takes the UNLOADED rate as its base. The
  // agreement's own arithmetic for combining the casual loading with a penalty
  // is uniformly additive: casual Saturday 175% (= 150 + 25) and Sunday 225%
  // (= 200 + 25) at cl 42(b), casual public holiday 275% (= 250 + 25). A
  // compounding reading would produce 187.5% / 250% / 312.5% and contradict
  // those printed figures. Overtime is the agreement's SOLE express compounding
  // carve-out (cl 32(h) and 32(j)(i): "overtime is to be calculated on the
  // casual loaded rate"), and overtime is not compiled as a conditional penalty.
  //
  // Scope is deliberately narrow — SHIFT_START_WINDOW only. MEAL_BREAK and
  // INTERRUPTED_MEAL_BREAK use cl 30's different "normal hourly rate" wording
  // and have NOT been primary-source verified for casuals; they keep the prior
  // compounding treatment (an over-pay, never an under-pay) pending their own
  // clause verification. See
  // docs/operations/clause-verifications/2026-07-25-medea-ea-cl44-casual-shift-loading-additive.md
  if (kind === 'SHIFT_START_WINDOW' && profile.casualPenaltyConvention === 'INCLUSIVE') {
    return casualMultiplierFor(profile, multiplierValue);
  }

  // Do NOT round to 3dp here: the inclusive-casual product needs up to 5dp
  // (e.g. 1.125 × 1.25 = 1.40625). Rounding the multiplier before the engine
  // subtracts the ordinary casual rate causes cent-level mis-pay. The engine +
  // reducer both use this exact value and round2 the final dollars, so they stay
  // byte-equal.
  return multiplierValue * (1 + profile.casualLoadingRate);
}

function sourceClauseRef(block: PayRuleBlock): string | undefined {
  const params = block.parameters ?? {};
  return (
    normalizedText(params.clauseRef) ??
    normalizedText(params.clause_ref) ??
    normalizedText(params.sourceClauseRef) ??
    normalizedText(params.source_clause_ref) ??
    undefined
  );
}

function booleanParam(value: unknown): boolean | null {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return null;
}

/**
 * Whether weekday/shift overtime multipliers apply to the loaded hourly rate.
 * Explicit block param wins; otherwise GS cl 32(c)(i) defaults on.
 *
 * 2026-09-17 re-derivation — MA000034 (Nurses) NEVER defaults on, for either
 * the `NEA_OVERTIME_TIERS` block code or a `22.22` clauseRef. The Nurses EA's
 * unnumbered "Payment For Overtime" paragraph names an unqualified "double
 * time"; cl 22.2 confines the cl 22.1 shift loading to ORDINARY hours; cl
 * 22.3/22.4 make weekend premiums substitutional, never cumulative upon the
 * loading. "cl 22.22" — the sole authority a Nurses OT-tier block has ever
 * cited for this default — is the UNRELIEVED-shift-worker relief provision,
 * not a rate-basis clause, and never supported a loaded-rate reading (it
 * withdraws the whole `+$44.78` Hannah Mighall M3 uplift). GS EA cl 32(c)(i)
 * ("...the applicable hourly rate (including loadings)...") is the opposite
 * and is unaffected — see
 * docs/operations/clause-verifications/2026-09-17-aged-care-legislation-medea-nurses-ea-overtime-rate-basis-fix-shape.md
 */
export function overtimeOnLoadedRateForBlock(block: PayRuleBlock, baseAwardCode: string): boolean {
  const params = block.parameters ?? {};
  const explicit =
    booleanParam(params.overtimeOnLoadedRate) ?? booleanParam(params.overtime_on_loaded_rate);
  if (explicit !== null) return explicit;

  if (baseAwardCode === 'MA000034') return false;

  // Medea EA weekday OT tier blocks default loaded-rate on without clauseRef
  // (live pay_rule_blocks often omit the string; P43/P45 pins must not be the
  // only proof that production enables loaded-rate OT).
  if (block.block_code === 'GA_WEEKDAY_OVERTIME_TIERS' && baseAwardCode === 'MA000018') {
    return true;
  }

  const clauseRef = (sourceClauseRef(block) ?? '').toLowerCase();
  if (baseAwardCode === 'MA000018') {
    return clauseRef.includes('32(c)(i)');
  }
  return false;
}

function compileOvertimeRuleFromBlock(
  block: PayRuleBlock,
  profile: CompileProfile,
): CompiledOvertimeRule | null {
  const params = block.parameters ?? {};
  const firstTwoHoursMultiplier =
    numericParam(params.firstTwoHoursMultiplier) ?? numericParam(params.first_two_hours_multiplier);
  const afterTwoHoursMultiplier =
    numericParam(params.afterTwoHoursMultiplier) ?? numericParam(params.after_two_hours_multiplier);
  if (firstTwoHoursMultiplier === null || afterTwoHoursMultiplier === null) return null;

  return {
    sourceBlockCode: block.block_code,
    firstTwoHoursMultiplier,
    afterTwoHoursMultiplier,
    overtimeOnLoadedRate: overtimeOnLoadedRateForBlock(block, profile.baseAwardCode),
    ...(sourceClauseRef(block) ? { clauseRef: sourceClauseRef(block) } : {}),
  };
}

/** Only the complete, known daily pair carries this basis; cycle/casual blocks stay held. */
function compileGeneralStaffDailyOvertimePair(
  blocks: readonly PayRuleBlock[],
  profile: CompileProfile,
): CompiledOvertimeRule | null {
  if (profile.baseAwardCode !== 'MA000018') return null;
  const matches = (code: string, boundary: string, multiplier: number) => {
    // Count every row with this identity before validating. A conflicting or
    // disabled duplicate must not disappear from the ambiguity check.
    const rows = blocks.filter((block) => block.block_code === code);
    if (rows.length !== 1) return [];
    return rows.filter((block) => {
      const params = block.parameters ?? {};
      const thresholdMatches =
        boundary === 'firstHours'
          ? params.threshold === 'after_daily_ordinary_hours'
          : params.threshold === undefined || params.threshold === 'after_daily_ordinary_hours';
      const allowed = new Set([
        'threshold',
        boundary,
        'multiplier',
        'overtimeOnLoadedRate',
        'overtime_on_loaded_rate',
      ]);
      return (
        blockKind(block) === 'OVERTIME' &&
        blockStatus(block) === 'AUTOMATED' &&
        thresholdMatches &&
        Object.keys(params).every((key) => allowed.has(key)) &&
        numericParam(params[boundary]) === 2 &&
        numericParam(params.multiplier) === multiplier &&
        !(
          params.overtimeOnLoadedRate !== undefined &&
          params.overtime_on_loaded_rate !== undefined &&
          params.overtimeOnLoadedRate !== params.overtime_on_loaded_rate
        )
      );
    });
  };
  const first = matches('GA_DAILY_OT_FIRST_2', 'firstHours', 1.5);
  const after = matches('GA_DAILY_OT_AFTER_2', 'afterHours', 2);
  if (first.length !== 1 || after.length !== 1) return null;
  // An explicit false on either half remains an opt-out from the inferred basis.
  const enabled = [first[0]!, after[0]!].every((block) => {
    const params = block.parameters ?? {};
    return (
      (booleanParam(params.overtimeOnLoadedRate) ??
        booleanParam(params.overtime_on_loaded_rate)) !== false
    );
  });
  return {
    sourceBlockCode: first[0]!.block_code,
    firstTwoHoursMultiplier: 1.5,
    afterTwoHoursMultiplier: 2,
    overtimeOnLoadedRate: enabled,
    shiftLoadingIsAdditive: true,
  };
}

function startWindowParam(block: PayRuleBlock): readonly [string, string] | null {
  const params = block.parameters ?? {};
  const raw = params.startWindow ?? params.start_window;
  if (!Array.isArray(raw) || raw.length !== 2) return null;
  const start = normalizedText(raw[0]);
  const end = normalizedText(raw[1]);
  return start && end ? [start, end] : null;
}

/**
 * Window test mode for a shift-loading block's `startWindow`. Absent →
 * COMMENCEMENT (legacy). `SPAN` selects the cl 44 overlap test. Any other value
 * is ignored (treated as absent) so a typo can never silently change a mechanic.
 */
function windowModeParam(block: PayRuleBlock): CompiledShiftWindowMode | undefined {
  const params = block.parameters ?? {};
  const raw = normalizedText(params.windowMode) ?? normalizedText(params.window_mode);
  const upper = raw?.toUpperCase();
  if (upper === 'SPAN') return 'SPAN';
  if (upper === 'FINISH') return 'FINISH';
  if (upper === 'COMMENCEMENT') return 'COMMENCEMENT';
  return undefined;
}

/**
 * Blocks that declare a `windowMode` param whose value is not a recognised
 * mechanic (e.g. a typo `SPN`). Surfaced as structural-unsupported so the
 * version cannot activate — a silent default to COMMENCEMENT would under-pay
 * (a cl 44 span block that quietly becomes commencement pays $0 on overlap
 * shifts). Fail loud, never silent.
 */
function invalidWindowModeBlocks(blocks: PayRuleBlock[]): StructuralBlock[] {
  const out: StructuralBlock[] = [];
  for (const block of blocks) {
    if (blockStatus(block) === 'DISABLED') continue;
    const params = block.parameters ?? {};
    const raw = normalizedText(params.windowMode) ?? normalizedText(params.window_mode);
    if (raw == null) continue;
    const upper = raw.toUpperCase();
    if (upper !== 'SPAN' && upper !== 'FINISH' && upper !== 'COMMENCEMENT') {
      out.push({
        blockCode: block.block_code,
        blockKind: blockKind(block),
        automationStatus: blockStatus(block),
        reason: `INVALID_WINDOW_MODE:${raw}`,
        parameters: params,
      });
    }
  }
  return out;
}

function isMealBreakPenaltyBlock(block: PayRuleBlock): boolean {
  const params = block.parameters ?? {};
  const codeAndTitle = blockCodeAndTitle(block);
  const requiredFacts = new Set(
    (block.required_fact_codes ?? []).map((fact) => fact.toUpperCase()),
  );
  return (
    codeAndTitle.includes('MEAL_BREAK') &&
    (params.triggerContinuousHours !== undefined ||
      params.trigger_continuous_hours !== undefined ||
      requiredFacts.has('MEAL_BREAK_TAKEN') ||
      requiredFacts.has('CONTINUOUS_HOURS'))
  );
}

function isInterruptedMealBreakPenaltyBlock(block: PayRuleBlock): boolean {
  const codeAndTitle = blockCodeAndTitle(block);
  const requiredFacts = new Set(
    (block.required_fact_codes ?? []).map((fact) => fact.toUpperCase()),
  );
  return codeAndTitle.includes('INTERRUPTED') || requiredFacts.has('MEAL_BREAK_INTERRUPTED');
}

function breakTreatmentPremiumKind(
  block: PayRuleBlock,
): Extract<CompiledConditionalPenaltyKind, 'MEAL_BREAK' | 'INTERRUPTED_MEAL_BREAK'> | null {
  if (blockKind(block) !== 'BREAK_TREATMENT') return null;
  const params = block.parameters ?? {};
  const configured = normalizedUpper(
    params.premiumKind ?? params.premium_kind ?? params.conditionalPenaltyKind,
  );
  if (configured === 'MEAL_BREAK' || configured === 'INTERRUPTED_MEAL_BREAK') {
    return configured;
  }
  return null;
}

function isAgedCareShiftStartWindowBlock(block: PayRuleBlock, profile: CompileProfile): boolean {
  if (profile.baseAwardCode !== 'MA000018') return false;
  if (!startWindowParam(block)) return false;
  const band = inferredPenaltyBand(block);
  return band === 'WEEKDAY_AFTERNOON' || band === 'WEEKDAY_NIGHT';
}

function isPublicHolidayNotWorkedBlock(block: PayRuleBlock): boolean {
  if (!nonWorkedPenaltyVariantReason(block)) return false;
  const { normalized, tokens } = normalizedTokenText(blockCodeAndTitle(block));
  const tokenSet = new Set(tokens);
  return (
    normalized.includes('PUBLIC_HOLIDAY') ||
    normalized.includes('PUBLICHOLIDAY') ||
    tokenSet.has('PH')
  );
}

function conditionalMultiplierValue(
  block: PayRuleBlock,
  collectedMultipliers: readonly CollectedBandMultiplier[],
): number | null {
  const params = block.parameters ?? {};
  return (
    numericParam(params.multiplier) ??
    numericParam(params.rateMultiplier) ??
    numericParam(params.penaltyMultiplier) ??
    numericParam(params.workedMultiplier) ??
    collectedMultipliers[0]?.multiplier ??
    null
  );
}

/** Public-holiday not-worked lines must never fall back to worked multipliers. */
function publicHolidayNotWorkedMultiplierValue(block: PayRuleBlock): number | null {
  const params = block.parameters ?? {};
  return (
    numericParam(params.notWorkedMultiplier) ??
    numericParam(params.not_worked_multiplier) ??
    numericParam(params.nonWorkedMultiplier) ??
    numericParam(params.non_worked_multiplier) ??
    null
  );
}

function conditionalMultiplierUnsupportedReason(multiplierValue: number | null): string | null {
  if (multiplierValue !== null && multiplierValue < 1) {
    return CONDITIONAL_MULTIPLIER_TOTAL_RATE_REASON;
  }
  return null;
}

function conditionalEmploymentTypes(
  block: PayRuleBlock,
  defaultPermanentOnly = false,
): CompiledPenaltyEmployment[] {
  const explicit = employmentTypeForMultiplier(block);
  if (explicit) return [explicit];
  return defaultPermanentOnly ? ['PERMANENT'] : [...EMPLOYMENT_ORDER];
}

function addConditionalPenaltyLines(
  lines: CompiledConditionalPenaltyLine[],
  requiredConditionalPenaltyCodes: Set<string>,
  profile: CompileProfile,
  block: PayRuleBlock,
  kind: CompiledConditionalPenaltyKind,
  multiplierValue: number,
  options: {
    permanentOnly?: boolean;
    startWindow?: readonly [string, string] | null;
    triggerContinuousHours?: number | null;
  } = {},
): void {
  requiredConditionalPenaltyCodes.add(block.block_code);
  const description = normalizedText(block.title) ?? block.block_code;
  const clauseRef = sourceClauseRef(block);
  const employeeCategories = conditionalEmployeeCategories(block);
  const windowMode = windowModeParam(block);
  const targetEmploymentType = employmentTypeForMultiplier(block);
  for (const employmentType of conditionalEmploymentTypes(block, options.permanentOnly ?? false)) {
    lines.push({
      blockCode: block.block_code,
      blockKind: blockKind(block),
      kind,
      employmentType,
      multiplier: conditionalTotalMultiplier(
        profile,
        multiplierValue,
        employmentType,
        kind,
        targetEmploymentType,
      ),
      description,
      ...(employeeCategories.length > 0 ? { employeeCategories } : {}),
      ...(windowMode ? { windowMode } : {}),
      ...(clauseRef ? { clauseRef } : {}),
      ...(options.startWindow ? { startWindow: options.startWindow } : {}),
      ...(options.triggerContinuousHours !== null && options.triggerContinuousHours !== undefined
        ? { triggerContinuousHours: options.triggerContinuousHours }
        : {}),
    });
  }
}

function nonCtxReadablePenaltyReason(
  profile: CompileProfile,
  bands: readonly PenaltyBand[],
): string | null {
  const unsupportedBands = [
    ...new Set(bands.filter((band) => !profile.ctxReadableBands.includes(band))),
  ].sort((a, b) => bandOrder(a) - bandOrder(b));
  if (unsupportedBands.length === 0) return null;
  if (
    profile.baseAwardCode === 'MA000018' &&
    unsupportedBands.every((band) => band === 'WEEKDAY_AFTERNOON' || band === 'WEEKDAY_NIGHT')
  ) {
    return 'AGED_CARE_COMMENCEMENT_SPAN_REBUILD_REQUIRED';
  }
  return `PENALTY_BANDS_NOT_CTX_READABLE:${unsupportedBands.join(',')}`;
}

function roundMultiplier(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function collectDirectBandMultipliers(block: PayRuleBlock): CollectedBandMultiplier[] {
  const params = block.parameters ?? {};
  const collected: CollectedBandMultiplier[] = [];

  for (const [band, aliases] of DIRECT_BAND_PARAM_ALIASES) {
    for (const alias of aliases) {
      const multiplier = numericParam(params[alias]);
      if (multiplier !== null) {
        collected.push({
          band,
          multiplier,
          employmentType: employmentTypeForMultiplier(block, alias),
        });
      }
    }
  }

  for (const alias of ['multiplier', 'rateMultiplier', 'penaltyMultiplier', 'workedMultiplier']) {
    const multiplier = numericParam(params[alias]);
    const band = multiplier !== null ? inferredPenaltyBand(block, alias) : null;
    if (band && multiplier !== null) {
      collected.push({
        band,
        multiplier,
        employmentType: employmentTypeForMultiplier(block, alias),
      });
    }
  }

  const entries = params.penalties ?? params.penaltyMultipliers;
  if (entries && typeof entries === 'object' && !Array.isArray(entries)) {
    for (const [rawBand, rawValue] of Object.entries(entries as Record<string, unknown>)) {
      const band = canonicalPenaltyBand(rawBand);
      const multiplier = numericParam(rawValue);
      if (band && multiplier !== null) {
        collected.push({
          band,
          multiplier,
          employmentType: employmentTypeForMultiplier(block, rawBand),
        });
      }
    }
  }

  return collected;
}

function requiredAllowanceCodesForBlock(block: PayRuleBlock): string[] {
  const params = block.parameters ?? {};
  const codes = new Set<string>();
  addUniqueSorted(codes, normalizedUpper(params.allowanceCode));
  addUniqueSorted(codes, normalizedUpper(params.code));
  for (const code of stringArray(params.allowanceCodes))
    addUniqueSorted(codes, normalizedUpper(code));
  for (const code of stringArray(params.requiredAllowanceCodes)) {
    addUniqueSorted(codes, normalizedUpper(code));
  }
  const allowances = params.allowances;
  if (allowances && typeof allowances === 'object' && !Array.isArray(allowances)) {
    for (const code of Object.keys(allowances as Record<string, unknown>)) {
      addUniqueSorted(codes, normalizedUpper(code));
    }
  }
  return [...codes].sort();
}

function hasStructuralShape(
  block: PayRuleBlock,
  compiledBands: CollectedBandMultiplier[],
): string | null {
  const params = block.parameters ?? {};
  const codeAndTitle = blockCodeAndTitle(block);
  const explicitBand = canonicalPenaltyBand(
    params.band ?? params.penaltyBand ?? params.shiftBand ?? params.dayType ?? params.day_type,
  );

  if (
    params.weekendAsOvertime === true ||
    params.weekend_as_overtime === true ||
    codeAndTitle.includes('WEEKEND_AS_OT') ||
    codeAndTitle.includes('WEEKEND_OT')
  ) {
    return 'WEEKEND_AS_OVERTIME_REQUIRES_ENGINE_INTERPRETER';
  }
  if (
    params.thresholdHours !== undefined ||
    params.thresholdMinutes !== undefined ||
    params.firstTwoHoursMultiplier !== undefined ||
    params.afterTwoHoursMultiplier !== undefined ||
    params.fortnightThresholdHours !== undefined ||
    codeAndTitle.includes('OVERTIME') ||
    codeAndTitle.includes('FORTNIGHT')
  ) {
    return 'BANDED_OR_CYCLE_THRESHOLD_REQUIRES_ENGINE_INTERPRETER';
  }
  if (
    (params.replacesOtherShiftPenalties === true ||
      params.replaces_other_shift_penalties === true) &&
    explicitBand !== 'PERMANENT_NIGHT_SHIFT' &&
    !compiledBands.some(({ band }) => band === 'PERMANENT_NIGHT_SHIFT')
  ) {
    return 'REPLACES_OTHER_SHIFT_PENALTIES_REQUIRES_STRUCTURAL_SUPPORT';
  }
  if (
    params.perHourOnCall === true ||
    params.per_hour_on_call === true ||
    codeAndTitle.includes('REMOTE_CALL') ||
    codeAndTitle.includes('ON_CALL')
  ) {
    return 'PER_HOUR_ON_CALL_REQUIRES_ENGINE_INTERPRETER';
  }
  return null;
}

function manualContract(
  block: PayRuleBlock,
  reason: string,
  allowanceCodes: string[] = [],
): ManualBlockContract {
  return {
    blockCode: block.block_code,
    blockKind: blockKind(block),
    automationStatus: blockStatus(block),
    requiredFactCodes: [...(block.required_fact_codes ?? [])].sort(),
    exportComponentCodes: [...(block.export_component_codes ?? [])].sort(),
    requiredAllowanceCodes: [...allowanceCodes].sort(),
    reason,
  };
}

function structuralBlock(block: PayRuleBlock, reason: string): StructuralBlock {
  return {
    blockCode: block.block_code,
    blockKind: blockKind(block),
    automationStatus: blockStatus(block),
    reason,
    parameters: block.parameters ?? {},
  };
}

function pushStructuralUnsupportedOnce(
  structuralUnsupported: StructuralBlock[],
  block: PayRuleBlock,
  reason: string,
): void {
  const blockCode = block.block_code;
  if (
    structuralUnsupported.some((entry) => entry.blockCode === blockCode && entry.reason === reason)
  ) {
    return;
  }
  structuralUnsupported.push(structuralBlock(block, reason));
}

type ShiftCharacterRules = NonNullable<CompiledEbaOverlay['shiftCharacterRules']>;
type ShiftCharacterWindow = ShiftCharacterRules['afternoon'];

// Hour-of-day parser [0, 24] — unlike numericParam (multipliers, > 0), a shift
// window hour may legitimately be 0 (midnight).
function hourParam(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(n) && n >= 0 && n <= 24 ? n : null;
}

export type OvernightDayType = 'weekday' | 'saturday' | 'sunday' | 'publicHoliday';

export interface OvernightDayTypeBand {
  dayType: OvernightDayType;
  hours: number;
}

const POSITIVE_HOURS_EPSILON = 1 / 60_000;

function hourFromShiftClock(value: string): number | null {
  const [hourRaw = '', minuteRaw = '0'] = value.split(':');
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (hour === 24 && minute === 0) return 24;
  if (hour < 0 || hour > 23 || minute < 0 || minute >= 60) return null;
  return hour + minute / 60;
}

function roundShiftHours(hours: number): number {
  return Math.round(hours * 10_000) / 10_000;
}

function positiveShiftHours(hours: number): number {
  return Number.isFinite(hours) && hours > POSITIVE_HOURS_EPSILON ? roundShiftHours(hours) : 0;
}

function shiftMinutesFromHours(hours: number): number {
  return Math.round(positiveShiftHours(hours) * 60);
}

function shiftHoursFromMinutes(minutes: number): number {
  return roundShiftHours(Math.max(0, minutes) / 60);
}

/**
 * Collapse day-type band minutes to strictly positive entries. Zero- or
 * negative-width midnight segments must never reach the engine (M5).
 */
export function compactPositiveOvernightDayTypeBands(
  bands: readonly OvernightDayTypeBand[],
): OvernightDayTypeBand[] {
  const merged = new Map<OvernightDayType, number>();
  for (const band of bands) {
    const hours = positiveShiftHours(band.hours);
    if (hours <= 0) continue;
    merged.set(band.dayType, roundShiftHours((merged.get(band.dayType) ?? 0) + hours));
  }
  return [...merged.entries()].map(([dayType, hours]) => ({ dayType, hours }));
}

export function sumOvernightDayTypeBandMinutes(bands: readonly OvernightDayTypeBand[]): number {
  return compactPositiveOvernightDayTypeBands(bands).reduce(
    (sum, band) => sum + shiftMinutesFromHours(band.hours),
    0,
  );
}

/**
 * Split an overnight shift into calendar day-type bands at local midnight.
 * Only positive-duration bands are returned; their hours sum to `totalHours`.
 */
export function splitOvernightShiftDayTypeBands(input: {
  shiftStartTime: string;
  shiftEndTime: string;
  totalHours: number;
  startDayType: OvernightDayType;
  endDayType?: OvernightDayType;
}): OvernightDayTypeBand[] {
  const totalMinutes = shiftMinutesFromHours(input.totalHours);
  if (totalMinutes <= 0) return [];

  const startHour = hourFromShiftClock(input.shiftStartTime);
  const endHour = hourFromShiftClock(input.shiftEndTime);
  if (startHour === null || endHour === null) {
    return compactPositiveOvernightDayTypeBands([
      { dayType: input.startDayType, hours: shiftHoursFromMinutes(totalMinutes) },
    ]);
  }

  const minutesUntilMidnight = Math.max(0, Math.round((24 - startHour) * 60));
  const endCrossesMidnight = endHour <= startHour || endHour === 24;
  const durationCrossesMidnight = totalMinutes > minutesUntilMidnight + 1;
  const crossesMidnight = endCrossesMidnight || durationCrossesMidnight;

  if (!crossesMidnight) {
    return compactPositiveOvernightDayTypeBands([
      { dayType: input.startDayType, hours: shiftHoursFromMinutes(totalMinutes) },
    ]);
  }

  const beforeMidnightMinutes = Math.min(totalMinutes, minutesUntilMidnight);
  const afterMidnightMinutes = totalMinutes - beforeMidnightMinutes;
  const endDayType = input.endDayType ?? input.startDayType;

  return compactPositiveOvernightDayTypeBands([
    ...(beforeMidnightMinutes > 0
      ? [{ dayType: input.startDayType, hours: shiftHoursFromMinutes(beforeMidnightMinutes) }]
      : []),
    ...(afterMidnightMinutes > 0
      ? [{ dayType: endDayType, hours: shiftHoursFromMinutes(afterMidnightMinutes) }]
      : []),
  ]);
}

function parseShiftCharacterWindow(raw: unknown): ShiftCharacterWindow | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const finishAfterHour = hourParam(r.finishAfterHour ?? r.finish_after_hour);
  const finishAtOrBeforeHour = hourParam(r.finishAtOrBeforeHour ?? r.finish_at_or_before_hour);
  if (finishAfterHour === null || finishAtOrBeforeHour === null) return null;
  const commenceAtOrAfterHour = hourParam(r.commenceAtOrAfterHour ?? r.commence_at_or_after_hour);
  const finishStrictlyBefore =
    (r.finishStrictlyBefore ?? r.finish_strictly_before) === true ? true : undefined;
  return {
    ...(commenceAtOrAfterHour !== null ? { commenceAtOrAfterHour } : {}),
    finishAfterHour,
    finishAtOrBeforeHour,
    ...(finishStrictlyBefore ? { finishStrictlyBefore } : {}),
  };
}

/**
 * Extract EA-declared afternoon/night shift-character windows from a block's
 * `shiftCharacterRules` parameter (both afternoon AND night must parse, else
 * ignored — never a partial mechanic). Data-driven, provider-agnostic.
 */
function parseShiftCharacterRulesFromBlocks(
  blocks: PayRuleBlock[],
): ShiftCharacterRules | undefined {
  for (const block of blocks) {
    if (blockStatus(block) === 'DISABLED') continue;
    const params = block.parameters ?? {};
    const raw = params.shiftCharacterRules ?? params.shift_character_rules;
    if (!raw || typeof raw !== 'object') continue;
    const afternoon = parseShiftCharacterWindow((raw as Record<string, unknown>).afternoon);
    const night = parseShiftCharacterWindow((raw as Record<string, unknown>).night);
    if (afternoon && night) return { afternoon, night };
  }
  return undefined;
}

export function compileEbaOverlay(
  blocks: PayRuleBlock[],
  baseAwardCode: string,
): CompiledEbaOverlay {
  const profile = EBA_COMPILE_PROFILES[baseAwardCode];
  if (!profile) {
    const activeBlocks = blocks.filter((block) => blockStatus(block) !== 'DISABLED');
    const automatedPenaltyBlocks = activeBlocks.filter(
      (block) => blockStatus(block) === 'AUTOMATED' && blockKind(block) === 'PENALTY',
    );
    return {
      penalties: [],
      allowances: [],
      conditionalPenalties: [],
      overtimeRules: [],
      requiredBands: [],
      requiredAllowanceCodes: [],
      requiredConditionalPenaltyCodes: [],
      manualContracts: activeBlocks
        .filter((block) => {
          const status = blockStatus(block);
          const kind = blockKind(block);
          return (
            status === 'MANUAL_ONLY' ||
            status === 'UNSUPPORTED' ||
            (status === 'AUTOMATED' && kind === 'ALLOWANCE')
          );
        })
        .map((block) => manualContract(block, `NO_COMPILE_PROFILE_FOR_${baseAwardCode}`))
        .sort((a, b) => a.blockCode.localeCompare(b.blockCode)),
      structuralUnsupported: automatedPenaltyBlocks
        .map((block) => ({
          blockCode: block.block_code,
          blockKind: blockKind(block),
          automationStatus: blockStatus(block),
          reason: `NO_COMPILE_PROFILE_FOR_${baseAwardCode}`,
          parameters: block.parameters ?? {},
        }))
        .sort((a, b) => a.blockCode.localeCompare(b.blockCode)),
    };
  }

  const penalties = new Map<string, CompiledPenaltyLine>();
  const penaltySourceBlocks = new Map<string, PayRuleBlock>();
  const penaltyLineSpecificities = new Map<string, PenaltyLineSpecificity>();
  const requiredBands = new Set<string>();
  const requiredAllowanceCodes = new Set<string>();
  const conditionalPenalties: CompiledConditionalPenaltyLine[] = [];
  const dailyOvertimePair = compileGeneralStaffDailyOvertimePair(blocks, profile);
  const overtimeRules: CompiledOvertimeRule[] = dailyOvertimePair ? [dailyOvertimePair] : [];
  const requiredConditionalPenaltyCodes = new Set<string>();
  const manualContracts: ManualBlockContract[] = [];
  const structuralUnsupported: StructuralBlock[] = [];

  for (const block of blocks) {
    const status = blockStatus(block);
    if (status === 'DISABLED') continue;

    const kind = blockKind(block);
    const params = block.parameters ?? {};
    const allowanceCodes = requiredAllowanceCodesForBlock(block);
    if (
      profile.baseAwardCode === 'MA000018' &&
      ['GA_DAILY_OT_FIRST_2', 'GA_DAILY_OT_AFTER_2'].includes(block.block_code) &&
      !dailyOvertimePair
    ) {
      pushStructuralUnsupportedOnce(structuralUnsupported, block, 'GA_DAILY_OVERTIME_PAIR_INVALID');
      continue;
    }

    if (status === 'MANUAL_ONLY') {
      for (const code of allowanceCodes) requiredAllowanceCodes.add(code);
      manualContracts.push(manualContract(block, 'MANUAL_ONLY_BLOCK', allowanceCodes));
      continue;
    }

    if (status !== 'AUTOMATED') {
      structuralUnsupported.push({
        blockCode: block.block_code,
        blockKind: kind,
        automationStatus: status,
        reason: `AUTOMATION_STATUS_${status}_NOT_COMPILABLE`,
        parameters: params,
      });
      continue;
    }

    if (kind === 'ALLOWANCE') {
      // Keep in lockstep with eba-drift-scan.ts COMPILABLE_BLOCK_KINDS:
      // Phase 1 surfaces allowance requirements but does not emit allowance
      // rows. Phase 2 must enable scanner ALLOWANCE selection and populate
      // compiled allowances in the same change.
      for (const code of allowanceCodes) requiredAllowanceCodes.add(code);
      manualContracts.push(
        manualContract(block, 'ALLOWANCE_LINE_EMISSION_DEFERRED_PHASE_1', allowanceCodes),
      );
      continue;
    }

    const configuredBreakTreatmentPremiumKind = breakTreatmentPremiumKind(block);
    if (kind !== 'PENALTY' && configuredBreakTreatmentPremiumKind === null) {
      continue;
    }

    const multipliers = collectDirectBandMultipliers(block);
    const conditionalMultiplier = conditionalMultiplierValue(block, multipliers);
    const conditionalMultiplierReason =
      conditionalMultiplierUnsupportedReason(conditionalMultiplier);

    if (configuredBreakTreatmentPremiumKind !== null) {
      if (conditionalMultiplier === null) {
        pushStructuralUnsupportedOnce(
          structuralUnsupported,
          block,
          'CONDITIONAL_PENALTY_MULTIPLIER_REQUIRED',
        );
        continue;
      }
      if (conditionalMultiplierReason) {
        pushStructuralUnsupportedOnce(structuralUnsupported, block, conditionalMultiplierReason);
        continue;
      }
      addConditionalPenaltyLines(
        conditionalPenalties,
        requiredConditionalPenaltyCodes,
        profile,
        block,
        configuredBreakTreatmentPremiumKind,
        conditionalMultiplier,
        {
          triggerContinuousHours:
            numericParam(params.triggerContinuousHours) ??
            numericParam(params.trigger_continuous_hours),
        },
      );
      continue;
    }

    const nonWorkedReason = nonWorkedPenaltyVariantReason(block);
    if (nonWorkedReason) {
      if (isPublicHolidayNotWorkedBlock(block)) {
        const notWorkedMultiplier = publicHolidayNotWorkedMultiplierValue(block);
        const notWorkedMultiplierReason =
          conditionalMultiplierUnsupportedReason(notWorkedMultiplier);
        if (notWorkedMultiplier === null) {
          pushStructuralUnsupportedOnce(
            structuralUnsupported,
            block,
            'PUBLIC_HOLIDAY_NOT_WORKED_MULTIPLIER_REQUIRED',
          );
          continue;
        }
        if (notWorkedMultiplierReason) {
          pushStructuralUnsupportedOnce(structuralUnsupported, block, notWorkedMultiplierReason);
          continue;
        }
        addConditionalPenaltyLines(
          conditionalPenalties,
          requiredConditionalPenaltyCodes,
          profile,
          block,
          'PUBLIC_HOLIDAY_NOT_WORKED',
          notWorkedMultiplier,
          { permanentOnly: true },
        );
        continue;
      }
      pushStructuralUnsupportedOnce(structuralUnsupported, block, nonWorkedReason);
      continue;
    }

    if (isInterruptedMealBreakPenaltyBlock(block) && conditionalMultiplier !== null) {
      if (conditionalMultiplierReason) {
        pushStructuralUnsupportedOnce(structuralUnsupported, block, conditionalMultiplierReason);
        continue;
      }
      addConditionalPenaltyLines(
        conditionalPenalties,
        requiredConditionalPenaltyCodes,
        profile,
        block,
        'INTERRUPTED_MEAL_BREAK',
        conditionalMultiplier,
      );
      continue;
    }

    if (isMealBreakPenaltyBlock(block) && conditionalMultiplier !== null) {
      if (conditionalMultiplierReason) {
        pushStructuralUnsupportedOnce(structuralUnsupported, block, conditionalMultiplierReason);
        continue;
      }
      addConditionalPenaltyLines(
        conditionalPenalties,
        requiredConditionalPenaltyCodes,
        profile,
        block,
        'MEAL_BREAK',
        conditionalMultiplier,
        {
          triggerContinuousHours:
            numericParam(params.triggerContinuousHours) ??
            numericParam(params.trigger_continuous_hours),
        },
      );
      continue;
    }

    if (isAgedCareShiftStartWindowBlock(block, profile) && conditionalMultiplier !== null) {
      if (conditionalMultiplierReason) {
        pushStructuralUnsupportedOnce(structuralUnsupported, block, conditionalMultiplierReason);
        continue;
      }
      addConditionalPenaltyLines(
        conditionalPenalties,
        requiredConditionalPenaltyCodes,
        profile,
        block,
        'SHIFT_START_WINDOW',
        conditionalMultiplier,
        { startWindow: startWindowParam(block) },
      );
      continue;
    }

    const structuralReason = hasStructuralShape(block, multipliers);
    if (structuralReason) {
      if (structuralReason === 'BANDED_OR_CYCLE_THRESHOLD_REQUIRES_ENGINE_INTERPRETER') {
        const overtimeRule = compileOvertimeRuleFromBlock(block, profile);
        if (overtimeRule) overtimeRules.push(overtimeRule);
      }
      structuralUnsupported.push({
        blockCode: block.block_code,
        blockKind: kind,
        automationStatus: status,
        reason: structuralReason,
        parameters: params,
      });
      continue;
    }
    if (multipliers.length === 0) {
      structuralUnsupported.push({
        blockCode: block.block_code,
        blockKind: kind,
        automationStatus: status,
        reason: 'PENALTY_BLOCK_YIELDED_NO_RECOGNIZED_MULTIPLIERS',
        parameters: params,
      });
      continue;
    }

    const unsupportedBandReason = nonCtxReadablePenaltyReason(
      profile,
      multipliers.map(({ band }) => band),
    );
    if (unsupportedBandReason) {
      structuralUnsupported.push({
        blockCode: block.block_code,
        blockKind: kind,
        automationStatus: status,
        reason: unsupportedBandReason,
        parameters: params,
      });
    }

    for (const { band, multiplier, employmentType } of multipliers) {
      addPenaltyLine(
        penalties,
        penaltySourceBlocks,
        penaltyLineSpecificities,
        requiredBands,
        structuralUnsupported,
        profile,
        block,
        band,
        multiplier,
        employmentType,
      );
    }
  }

  const compiledShiftCharacterRules = parseShiftCharacterRulesFromBlocks(blocks);

  return {
    penalties: [...penalties.values()].sort(
      (a, b) =>
        bandOrder(a.band) - bandOrder(b.band) ||
        EMPLOYMENT_ORDER.indexOf(a.employmentType) - EMPLOYMENT_ORDER.indexOf(b.employmentType) ||
        (a.classificationStream ?? '').localeCompare(b.classificationStream ?? '') ||
        a.sourceBlockCode.localeCompare(b.sourceBlockCode),
    ),
    allowances: [],
    conditionalPenalties: conditionalPenalties.sort(
      (a, b) =>
        a.kind.localeCompare(b.kind) ||
        a.blockCode.localeCompare(b.blockCode) ||
        EMPLOYMENT_ORDER.indexOf(a.employmentType) - EMPLOYMENT_ORDER.indexOf(b.employmentType),
    ),
    overtimeRules: overtimeRules.sort((a, b) => a.sourceBlockCode.localeCompare(b.sourceBlockCode)),
    requiredBands: [...requiredBands].sort((a, b) => bandOrder(a) - bandOrder(b)),
    requiredAllowanceCodes: [...requiredAllowanceCodes].sort(),
    requiredConditionalPenaltyCodes: [...requiredConditionalPenaltyCodes].sort(),
    manualContracts: manualContracts.sort((a, b) => a.blockCode.localeCompare(b.blockCode)),
    structuralUnsupported: [...structuralUnsupported, ...invalidWindowModeBlocks(blocks)].sort(
      (a, b) => a.blockCode.localeCompare(b.blockCode),
    ),
    ...(compiledShiftCharacterRules ? { shiftCharacterRules: compiledShiftCharacterRules } : {}),
  };
}

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = stableValue((value as Record<string, unknown>)[key]);
        return acc;
      }, {});
  }
  return value;
}

function stableStringify(value: unknown): string {
  return JSON.stringify(stableValue(value));
}

export function rulePackHash(value: unknown): string {
  const input = stableStringify(value);
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  const mask = 0xffffffffffffffffn;

  for (let i = 0; i < input.length; i++) {
    hash ^= BigInt(input.charCodeAt(i));
    hash = (hash * prime) & mask;
  }

  return `fnv1a64:${hash.toString(16).padStart(16, '0')}`;
}

export type { AllowanceBasis, PenaltyBand };
